/**
 * Everything the app says about something it has just DONE.
 *
 * ⚠️ THE OUTCOME OF AN ACTION IS A TOAST. THE STATE OF A PAGE IS NOT.
 * That is the whole rule, and it is the one this module exists to make hard to
 * break. Before it, every page invented its own answer: a green `Alert` under a
 * form, a muted `<p>` beside a button, a `{ ok, text }` object rendered in one
 * of two colours, or the button's own label turning into «Сохранение…». Four
 * vocabularies for one idea, and none of them agreed on where to look.
 *
 * The split that decides which is which:
 *
 *   an ACTION outcome  — «Сохранено», «Не удалось отправить», «бот промолчал»
 *                        → a toast. It is news about a thing that just
 *                          happened, it is read once, and it must not still be
 *                          on screen the next time the same button is pressed.
 *
 *   a PAGE state       — «не удалось загрузить состав»
 *                        → an inline `Alert`, next to the empty space it
 *                          explains. A toast cannot do this job: it leaves
 *                          after four seconds and the reader is then looking at
 *                          a blank card with no account of why.
 *
 * The message a failure should CARRY is `errorText` in `lib/errors.ts`, which
 * is a separate module because it has to be testable without a DOM.
 */

import type { ReactNode } from 'react';
import { toast } from '@heroui/react';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
} from '@tabler/icons-react';

/**
 * How long each kind stays.
 *
 * HeroUI's default is 4s, which is right for «Сохранено» — one word, already
 * expected, and the reader is looking at the form that produced it.
 *
 * It is wrong for everything else here. A failure is unexpected, is a whole
 * sentence, and is frequently the server explaining what to do instead; the
 * quiet notes are longer still — «бот промолчал — ближайшее событие с лутом не
 * настолько близко…» is 130 characters that mean nothing if half of them are
 * missed. Both get double, which is still short enough that nothing lingers
 * over the page.
 */
const CONFIRM_MS = 4_000;
const READ_ME_MS = 8_000;

interface NotifyOptions {
  /** A second line under the title. For the detail that would bloat the title. */
  description?: ReactNode;
  /** Overrides the default for this kind. */
  timeout?: number;
}

/**
 * The indicators, from the same icon set as the rest of the app.
 *
 * HeroUI ships its own for every variant, and they are perfectly good — they
 * are simply not the family every other glyph on the screen belongs to, and a
 * toast is the one surface that appears on top of any page in the app.
 */
const INDICATORS = {
  success: <IconCircleCheck />,
  error: <IconAlertCircle />,
  warning: <IconAlertTriangle />,
  info: <IconInfoCircle />,
  note: <IconInfoCircle />,
} as const;

export const notify = {
  /** It worked, and there was nothing else to say about it. */
  success(message: ReactNode, options?: NotifyOptions) {
    toast.success(message, {
      indicator: INDICATORS.success,
      timeout: CONFIRM_MS,
      ...options,
    });
  },

  /**
   * It did not work.
   *
   * Takes the TEXT, not the thrown value — `errorText` is what turns one into
   * the other, and keeping them separate is what lets a caller add context the
   * exception never had.
   */
  error(message: ReactNode, options?: NotifyOptions) {
    toast.danger(message, {
      indicator: INDICATORS.error,
      timeout: READ_ME_MS,
      ...options,
    });
  },

  /** It worked, and somebody should still know about it. */
  warning(message: ReactNode, options?: NotifyOptions) {
    toast.warning(message, {
      indicator: INDICATORS.warning,
      timeout: READ_ME_MS,
      ...options,
    });
  },

  /**
   * Neither a success nor a failure: a report of what happened.
   *
   * ⚠️ DELIBERATELY UNCOLOURED, and this is the kind the loot announcements and
   * the bot's «промолчал» outcomes use. The bot staying quiet because no loot
   * event is close, because nothing moved, or because it is switched off is the
   * feature WORKING — painting those red teaches an admin to ignore the one
   * message that also reports the case where the group was never told.
   */
  note(message: ReactNode, options?: NotifyOptions) {
    toast(message, {
      indicator: INDICATORS.note,
      timeout: READ_ME_MS,
      ...options,
    });
  },

  /** Something happened that the reader did not ask for and should look at. */
  info(message: ReactNode, options?: NotifyOptions) {
    toast.info(message, {
      indicator: INDICATORS.info,
      timeout: READ_ME_MS,
      ...options,
    });
  },
};
